import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { CURRENCY_EURO } from "../../../util/Constants";
import Cards from "../Cards/Cards";
import { tempPaymentMethods, tempAccountStats } from "../../../demo/demo";
import './Card.css';

const plans = [
    { name: '3 Days', price: 25 },
    { name: '7 Days', price: 49 },
    { name: '30 Days', price: 89 },
]

const PREMIUM_PRICE = 49
const PREMIUM_PRODUCT_NAME = 'Order Premium Model'

const planIncludes = [
    {label: '5 Vertical Photos', icon: 'photo'},
    {label: '2 Vertical Videos', icon: 'video'},
    {label: 'Premium listing for your first month', icon: 'premium'},
    {label: 'Professional profile', icon: 'profile'},
    {label: 'Visible to thousands of visitors on Sadida', icon: 'visitors'}
]

const premiumIncludes = [
    'Top position in search results',
    'Featured on Sadida badge',
    'Priority exposure across the platform',
    'Increased visibility and customer inquiries'
]

const PlanIncludeIcon = ({type}) => {
    const iconProps = {
        className: 'plan-includes-icon',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: '1.6',
        'aria-hidden': 'true',
        'data-icon': type
    }

    if (type === 'photo') {
        return <svg {...iconProps}><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m4 17 4.5-4.5 3 3 2-2 6.5 5.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    }

    if (type === 'video') {
        return <svg {...iconProps}><rect x="3" y="5" width="13" height="14" rx="2" /><path d="m16 10 5-3v10l-5-3z" strokeLinejoin="round" /></svg>
    }

    if (type === 'premium') {
        return <svg {...iconProps}><path d="m4 8 4 3 4-7 4 7 4-3-1 10H5L4 8Z" strokeLinejoin="round" /><path d="M6 21h12" strokeLinecap="round" /></svg>
    }

    if (type === 'profile') {
        return <svg {...iconProps}><circle cx="12" cy="8" r="3" /><path d="M5 20c.7-3.2 3-5 7-5s6.3 1.8 7 5" strokeLinecap="round" /></svg>
    }

    return <svg {...iconProps}><path d="M2.5 12s3.5-5 9.5-5 9.5 5 9.5 5-3.5 5-9.5 5-9.5-5-9.5-5Z" strokeLinejoin="round" /><circle cx="12" cy="12" r="2.5" /></svg>
}

const PremiumFeatureIcon = () => (
    <svg className="premium-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.9-5.4 2.9 1-6-4.4-4.3 6.1-.9L12 3Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const PremiumCrownIcon = () => (
    <svg className="premium-crown" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="m3 7 4.5 3L12 4l4.5 6L21 7l-1.5 11h-15L3 7Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 21h14" strokeLinecap="round" />
    </svg>
)

const premiumDetailsFrom = (source) => {
    if (!source || typeof source !== 'object') return null
    if (source.premium && typeof source.premium === 'object') return source.premium
    if (source.data?.premium && typeof source.data.premium === 'object') return source.data.premium
    return source
}

const formatPremiumEndDate = (value) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toISOString().slice(0, 10)
}

const Card = ({ link = false, currency = 'euro', adBalance = null, availableBalance = null, paymentMethods = null, premium = null, plan: configuredPlan = undefined, campaignStatus = undefined, dashboardPlanLink = false, deferPlanBilling = false, planChangeWithoutCharge = false, onPlanChange = null, onCampaignStatusChange = null, onAddCard = null, modelUuid = null, onPremiumUpgrade = null}) => {

    const balance = adBalance !== null ? adBalance : 325
    const numericBalance = Number(balance)
    const positiveAdBalance = Number.isFinite(numericBalance) && numericBalance > 0 ? numericBalance : 0
    const availBal = availableBalance !== null ? availableBalance : tempAccountStats.availableBalance
    const methods = paymentMethods !== null ? paymentMethods : tempPaymentMethods
    const primaryPaymentMethod = methods.find((method) => method.default) || methods[0]

    const [plan, setPlan] = useState(configuredPlan === undefined ? '7 Days' : configuredPlan)
    const [showModal, setShowModal] = useState(false)
    const [modalStep, setModalStep] = useState('plans')
    const [selectedPlan, setSelectedPlan] = useState(null)
    const [selectedMethod, setSelectedMethod] = useState(null)
    const [autoTopUp, setAutoTopUp] = useState(false)
    const [showAutoPay, setShowAutoPay] = useState(false)
    const [planSaving, setPlanSaving] = useState(false)
    const [planError, setPlanError] = useState('')
    const [premiumSaving, setPremiumSaving] = useState(false)
    const [premiumError, setPremiumError] = useState('')

    useEffect(() => {
        if (configuredPlan !== undefined) setPlan(configuredPlan)
    }, [configuredPlan])

    const planName = typeof plan === 'string' ? plan.trim() : plan?.name || plan?.label || plan?.value || ''
    const hasPlan = Boolean(planName)
    const currencySymbol = currency === CURRENCY_EURO ? '€' : '$'
    const premiumDetails = premiumDetailsFrom(premium)
    const premiumEndDate = formatPremiumEndDate(premiumDetails?.expiresAt)
    const premiumIsActive = String(premiumDetails?.status || '').toLowerCase() === 'active'
        && (!premiumDetails?.expiresAt || new Date(premiumDetails.expiresAt).getTime() > Date.now())
    const autoPayEnabled = campaignStatus !== undefined ? campaignStatus === 'auto' : autoTopUp
    const displayZeroAutoPayBalance = autoPayEnabled && Number.isFinite(numericBalance) && numericBalance === 0
    const showAutoPayBalanceNotice = autoPayEnabled && Number.isFinite(Number(availBal)) && Number(availBal) > 0

    const planTotal = (selectedPlan) => Math.max(selectedPlan.price - positiveAdBalance, 0)
    const noChargePlanChange = deferPlanBilling || planChangeWithoutCharge

    const handleSelectPlan = (p) => {
        setSelectedPlan(p)
        if (!noChargePlanChange && planTotal(p) > 0) {
            setModalStep('fund')
        } else {
            setModalStep('summary')
            setSelectedMethod(null)
        }
    }

    const handleSelectMethod = (method) => {
        setSelectedMethod(method)
        setModalStep('summary')
    }

    const handleConfirm = async () => {
        if (!selectedPlan || planSaving) return
        setPlanSaving(true)
        setPlanError('')
        try {
            const persistedPlan = onPlanChange ? await onPlanChange(selectedPlan.name) : selectedPlan.name
            setPlan(persistedPlan || selectedPlan.name)
            handleClose()
        } catch (error) {
            setPlanError(error.message || 'We could not save your advertising plan.')
        } finally {
            setPlanSaving(false)
        }
    }

    const handlePremiumConfirm = async () => {
        if (premiumSaving || (premiumCardCharge > 0 && !primaryPaymentMethod)) return
        setPremiumSaving(true)
        setPremiumError('')
        try {
            if (onPremiumUpgrade) {
                await onPremiumUpgrade({
                    modelUuid,
                    product: PREMIUM_PRODUCT_NAME,
                    price: PREMIUM_PRICE,
                    advertisingBalance: positiveAdBalance,
                    advertisingBalanceAfterPurchase: premiumBalanceAfterCharge,
                    advertisingBalanceApplied: premiumBudgetApplied,
                    cardCharge: premiumCardCharge,
                    paymentMethodId: premiumCardCharge > 0 ? primaryPaymentMethod?.id : null
                })
            }
            handleClose()
        } catch (error) {
            setPremiumError(error.message || 'We could not activate Premium for this model.')
        } finally {
            setPremiumSaving(false)
        }
    }

    const handleClose = () => {
        setShowModal(false)
        setModalStep('plans')
        setSelectedPlan(null)
        setSelectedMethod(null)
    }

    const handleAddCard = () => {
        if (onAddCard) {
            handleClose()
            onAddCard()
            return
        }
        setModalStep('newcard')
    }

    const handleAutoPayClick = (event) => {
        event.stopPropagation()
        if (onCampaignStatusChange) {
            if (autoPayEnabled) onCampaignStatusChange('active')
            else setShowAutoPay(true)
            return
        }
        if (!autoTopUp) setShowAutoPay(true)
        else setAutoTopUp(false)
    }

    const needsFunding = !noChargePlanChange && selectedPlan && planTotal(selectedPlan) > 0
    const amountToCharge = selectedPlan ? planTotal(selectedPlan) : 0
    const premiumBudgetApplied = Math.min(positiveAdBalance, PREMIUM_PRICE)
    const premiumCardCharge = Math.max(PREMIUM_PRICE - premiumBudgetApplied, 0)
    const premiumBalanceAfterCharge = Math.max(positiveAdBalance - premiumBudgetApplied, 0)

    return (
        <>
            <div className={`balance frosted-glow${hasPlan ? '' : ' balance-no-plan'}${premiumIsActive ? ' premium-balance' : ''}`} onClick={link ? () => link() : undefined}>
                {premiumIsActive && <PremiumCrownIcon />}
                <div className="balance-name">
                    <span className="balance-icon"></span><span className="balance-name-text">Current Advertising Balance</span>
                </div>
                {hasPlan ? <>
                    <div className="balance-topright">
                        {dashboardPlanLink ? <Link to="/account/balance" className="balance-plan-link">Select advertising plan →</Link> : <>
                            <div className="balance-plan">
                                PLAN: {planName.toUpperCase()}
                                <span className="balance-plan-actions">
                                    <button type="button" className="plan-change" onClick={(e) => { e.stopPropagation(); setShowModal(true); }} aria-haspopup="dialog">Change</button>
                                    {premiumIsActive ? null : <button type="button" className="plan-premium-button" onClick={(e) => { e.stopPropagation(); setModalStep('premium'); setShowModal(true); }} aria-haspopup="dialog">
                                        <PlanIncludeIcon type="premium" />
                                        <span>Premium</span>
                                    </button>}
                                </span>
                            </div>
                            <div className="auto-topup" onClick={handleAutoPayClick}>
                                <span className={autoPayEnabled ? 'topup-toggle on' : 'topup-toggle'}></span>
                                <span className="topup-label">Auto-pay</span>
                            </div>
                        </>}
                    </div>
                    <div className="balance-num">{displayZeroAutoPayBalance ? '-' : `${currencySymbol}${balance.toFixed(2)}`}</div>
                    <div className="balance-info">
                        {primaryPaymentMethod ? <>
                            <div className="balance-card-type">
                                <Cards type={primaryPaymentMethod.type} size={25} />
                            </div>
                            <div className="balance-card-num">
                                **** {primaryPaymentMethod.last4}
                            </div>
                        </> : <div className="balance-card-num">No payment method on file</div>}
                    </div>
                    {showAutoPayBalanceNotice && <p className="auto-pay-balance-notice">We will automatically subtract the next plan from your account balance. If there is not enough, we will use the remaining balance and charge the rest to your default card.</p>}
                    {premiumIsActive && <div className="premium-account-status" role="status">
                        <PlanIncludeIcon type="premium" />
                        <span className="premium-account-status-copy">
                            <strong>Premium account</strong>
                            <span>{premiumEndDate ? `Active until ${premiumEndDate}` : 'Active'}</span>
                        </span>
                    </div>}
                </> : <button type="button" className="balance-plan-empty" onClick={(event) => { event.stopPropagation(); setShowModal(true); }} aria-label="Select an advertising plan">
                    <span className="balance-plan-empty-icon" aria-hidden="true">+</span>
                    <span>Select an advertising plan</span>
                </button>}
            </div>

            {showModal && (
                <div className="plan-modal-overlay" onClick={handleClose}>
                    <div
                        className={`plan-modal${['premium', 'premium-summary'].includes(modalStep) ? ' premium-modal' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        role={['premium', 'premium-summary'].includes(modalStep) ? 'dialog' : undefined}
                        aria-modal={['premium', 'premium-summary'].includes(modalStep) ? 'true' : undefined}
                        aria-labelledby={['premium', 'premium-summary'].includes(modalStep) ? 'premium-modal-title' : undefined}
                    >
                        <div className="plan-modal-header">
                            <h2 id={['premium', 'premium-summary'].includes(modalStep) ? 'premium-modal-title' : undefined}>{modalStep === 'plans' ? 'Change Plan' : modalStep === 'premium' ? 'Featured on Sadida (Optional Upgrade)' : modalStep === 'premium-summary' ? 'Confirm Premium Upgrade' : modalStep === 'summary' ? 'Summary' : modalStep === 'newcard' ? 'New Card' : 'Add Funds'}</h2>
                            {modalStep === 'premium' ? <button type="button" className="plan-modal-close" onClick={handleClose} aria-label="Close Featured on Sadida upgrade">&times;</button> : <span className="plan-modal-close" onClick={handleClose}>&times;</span>}
                        </div>

                        {modalStep === 'premium' && (
                            <div className="premium-modal-content">
                                <p className="premium-modal-intro">Take your profile to the next level by becoming a Featured Listing.</p>
                                <p className="premium-modal-copy">Your advertisement will appear at the top of the listings and receive the Featured on Sadida badge, giving your profile maximum visibility and helping you attract more customers.</p>
                                <div className="premium-upgrade-summary">
                                    <div>
                                        <span className="premium-upgrade-label">Featured Upgrade</span>
                                        <span className="premium-upgrade-note">Optional monthly upgrade</span>
                                    </div>
                                    <div className="premium-upgrade-price">
                                        <strong>+{currencySymbol}49</strong>
                                        <span>per month</span>
                                    </div>
                                </div>
                                <section className="premium-features" aria-labelledby="premium-features-title">
                                    <h3 id="premium-features-title">Includes</h3>
                                    <ul>
                                        {premiumIncludes.map((item) => (
                                            <li key={item}>
                                                <PremiumFeatureIcon />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                                <button type="button" className="premium-modal-close-action" onClick={() => setModalStep('premium-summary')}>Switch to Premium</button>
                            </div>
                        )}

                        {modalStep === 'premium-summary' && (
                            <>
                                <p className="plan-modal-desc">Please review and confirm your Featured on Sadida upgrade.</p>
                                <div className="summary-details premium-summary-details">
                                    <div className="summary-row">
                                        <span className="summary-label">Product</span>
                                        <span className="summary-value">{PREMIUM_PRODUCT_NAME}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Upgrade</span>
                                        <span className="summary-value">Featured on Sadida</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Current Plan</span>
                                        <span className="summary-value">{planName || 'No plan selected'}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Premium Price</span>
                                        <span className="summary-value">{currencySymbol}{PREMIUM_PRICE.toFixed(2)}/month</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Advertising Balance Applied</span>
                                        <span className="summary-value">-{currencySymbol}{premiumBudgetApplied.toFixed(2)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Remaining Advertising Balance</span>
                                        <span className="summary-value">{currencySymbol}{premiumBalanceAfterCharge.toFixed(2)}</span>
                                    </div>
                                    {premiumCardCharge > 0 && (
                                        <>
                                            <div className="summary-row premium-charge-row">
                                                <span className="summary-label">Amount to Charge</span>
                                                <span className="summary-value">{currencySymbol}{premiumCardCharge.toFixed(2)}</span>
                                            </div>
                                            <div className="summary-row">
                                                <span className="summary-label">Payment Method</span>
                                                <span className="summary-value">{primaryPaymentMethod ? `**** ${primaryPaymentMethod.last4}` : 'No payment method on file'}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {premiumError && <p className="premium-payment-warning" role="alert">{premiumError}</p>}
                                {premiumCardCharge > 0 && !primaryPaymentMethod && <p className="premium-payment-warning" role="alert">Add a payment method to charge the remaining {currencySymbol}{premiumCardCharge.toFixed(2)}.</p>}
                                <button type="button" className="card-form-submit premium-summary-confirm" onClick={handlePremiumConfirm} disabled={premiumSaving || (premiumCardCharge > 0 && !primaryPaymentMethod)}>{premiumSaving ? 'Processing…' : 'Confirm & Pay'}</button>
                                <button type="button" className="fund-back premium-summary-back" onClick={() => setModalStep('premium')}>← Back</button>
                            </>
                        )}

                        {modalStep === 'plans' && (
                            <>
                                <p className="plan-modal-desc">Select your advertising billing cycle</p>
                                <div className="plan-selection-layout">
                                    <div className="plan-options">
                                        {plans.map((p) => (
                                            <div
                                                key={p.name}
                                                className={[
                                                    'plan-card',
                                                    plan === p.name ? 'active' : '',
                                                    p.name === '30 Days' ? 'plan-card-featured' : ''
                                                ].filter(Boolean).join(' ')}
                                                onClick={() => handleSelectPlan(p)}
                                            >
                                                {p.name === '30 Days' && <span className="plan-card-badge">Best Value</span>}
                                                <div className="plan-card-name">{p.name}</div>
                                                <div className="plan-card-price">{currencySymbol}{p.price.toLocaleString()}</div>
                                                <div className="plan-card-period">per {p.name.toLowerCase()}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <section className="plan-includes" aria-labelledby="plan-includes-title">
                                        <h3 id="plan-includes-title">Includes</h3>
                                        <ul>
                                            {planIncludes.map((item) => (
                                                <li key={item.label}>
                                                    <PlanIncludeIcon type={item.icon} />
                                                    <span>{item.label}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                </div>
                            </>
                        )}

                        {modalStep === 'fund' && selectedPlan && (
                            <>
                                <p className="plan-modal-desc">
                                    Insufficient balance. You need {currencySymbol}{amountToCharge.toLocaleString(undefined, {minimumFractionDigits:2})} more to switch to the {selectedPlan.name} plan. The remaining amount will be automatically charged to your selected payment method.
                                </p>
                                <div className="fund-amount">
                                    <span className="fund-label">Amount to add</span>
                                    <span className="fund-value">{currencySymbol}{amountToCharge.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                                </div>

                                {availBal >= amountToCharge && (
                                    <div className="fund-convert" onClick={() => { setSelectedMethod({type:'transfer', last4:'Available Balance'}); setModalStep('summary'); }}>
                                        <div className="fund-convert-info">
                                            <span className="fund-convert-title">Use Available Balance</span>
                                            <span className="fund-convert-bal">{currencySymbol}{availBal.toLocaleString(undefined, {minimumFractionDigits:2})} available</span>
                                        </div>
                                        <span className="fund-convert-btn">Transfer</span>
                                    </div>
                                )}
                                <div className="fund-methods">
                                    <p className="fund-methods-label">Or pay with card</p>
                                    {methods.map((method) => (
                                        <div className="fund-method" key={method.id} onClick={() => handleSelectMethod(method)}>
                                            <Cards type={method.type} size={25} />
                                            <span className="fund-method-num">**** {method.last4}</span>
                                            <span className="fund-method-exp">Exp {method.expiry}</span>
                                        </div>
                                    ))}
                                    <div className="fund-method add-new" onClick={handleAddCard}>
                                        <span className="fund-method-add">+ Add New Card</span>
                                    </div>
                                </div>
                                <span className="fund-back" onClick={() => setModalStep('plans')}>← Back to plans</span>
                            </>
                        )}

                        {modalStep === 'newcard' && (
                            <>
                                <p className="plan-modal-desc">Enter your card details</p>
                                <div className="card-form">
                                    <div className="card-form-field">
                                        <label>Card Number</label>
                                        <input type="text" placeholder="1234 5678 9012 3456" maxLength="19" />
                                    </div>
                                    <div className="card-form-row">
                                        <div className="card-form-field">
                                            <label>Expiry</label>
                                            <input type="text" placeholder="MM/YY" maxLength="5" />
                                        </div>
                                        <div className="card-form-field">
                                            <label>CVC</label>
                                            <input type="text" placeholder="123" maxLength="4" />
                                        </div>
                                    </div>
                                    <div className="card-form-field">
                                        <label>Name on Card</label>
                                        <input type="text" placeholder="Full name" />
                                    </div>
                                    <span className="card-form-submit" onClick={() => { setSelectedMethod({type:'visa', last4:'New'}); setModalStep('summary'); }}>Add Card & Continue</span>
                                </div>
                                <span className="fund-back" onClick={() => setModalStep('fund')}>← Back to payment methods</span>
                            </>
                        )}

                        {modalStep === 'summary' && selectedPlan && (
                            <>
                                <p className="plan-modal-desc">{deferPlanBilling ? 'This campaign will be scheduled after your current campaign. You will not be charged until it becomes active.' : planChangeWithoutCharge ? 'Your current auto-pay campaign plan will be replaced immediately. You will not be charged, and its expiry date will not change.' : 'Please review and confirm your plan change'}</p>
                                <div className="summary-details">
                                    <div className="summary-row">
                                        <span className="summary-label">New Plan</span>
                                        <span className="summary-value">{selectedPlan.name}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Plan Cost</span>
                                        <span className="summary-value">{currencySymbol}{selectedPlan.price.toLocaleString(undefined, {minimumFractionDigits:2})}/{selectedPlan.name.toLowerCase()}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Current Balance</span>
                                        <span className="summary-value">{currencySymbol}{balance.toFixed(2)}</span>
                                    </div>
                                    {needsFunding && selectedMethod && (
                                        <>
                                            <div className="summary-row">
                                                <span className="summary-label">Amount to Charge</span>
                                                <span className="summary-value">{currencySymbol}{amountToCharge.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                                            </div>
                                            <div className="summary-row">
                                                <span className="summary-label">Payment Method</span>
                                                <span className="summary-value">**** {selectedMethod.last4}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {planError && <p className="plan-save-error" role="alert">{planError}</p>}
                                <span className="card-form-submit" onClick={handleConfirm} aria-busy={planSaving}>{planSaving ? 'Saving…' : needsFunding ? 'Confirm & Pay' : 'Confirm'}</span>
                                <span className="fund-back" onClick={() => setModalStep(needsFunding ? 'fund' : 'plans')}>← Back</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showAutoPay && (
                <div className="plan-modal-overlay" onClick={() => setShowAutoPay(false)}>
                    <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="plan-modal-header">
                            <h2>Enable Auto-pay</h2>
                            <span className="plan-modal-close" onClick={() => setShowAutoPay(false)}>&times;</span>
                        </div>
                        <p className="plan-modal-desc">By enabling auto-pay, your default card on file will be automatically charged when your advertising balance is insufficient. You can disable this at any time.</p>
                        <span className="card-form-submit" onClick={() => { if (onCampaignStatusChange) onCampaignStatusChange('auto'); else setAutoTopUp(true); setShowAutoPay(false); }}>I Agree, Enable Auto-pay</span>
                        <span className="fund-back" onClick={() => setShowAutoPay(false)}>Cancel</span>
                    </div>
                </div>
            )}
        </>
    )
}

export default Card;
